import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Modal from 'components/common/modal';
import Button from 'components/common/button';
import { Explanation } from 'components/common/form-components';
import { paintedDesignMode } from 'constants/config';
import { useDesignMode } from 'hooks/use-design-mode';
import { useUpdateUserPreferences } from 'stores/rq/user-preferences';
import { shouldShowNewLookModal } from 'utils/design-mode.utils';
import { EventNames, track } from 'utils/analytics.utils';

/**
 * The announcement half of the rollout; the banner beside it owns the way back to classic. It is a
 * one-shot per user, so "seen" is written server-side rather than to this browser's storage.
 */
const NewLookModal: React.FunctionComponent = () => {
  const { selectedDesign, isDesignSwitchAvailable, isResolved, preferences } = useDesignMode();
  const { mutate: updatePreferences } = useUpdateUserPreferences();
  const [dismissedThisSession, setDismissedThisSession] = useState(false);

  const shouldShow =
    !dismissedThisSession &&
    shouldShowNewLookModal({
      isResolved,
      isDesignSwitchAvailable,
      selectedDesign,
      painted: paintedDesignMode,
      hasSeen: Boolean(preferences?.newLookModalSeenAt)
    });

  if (!shouldShow) return null;

  /**
   * Closing is local, and deliberately not conditional on the write landing. Leaving it to the
   * preference would read better - the modal would only close when it will stay closed - but it makes
   * the write a single point of failure for a mask over the whole page. A frontend deployed ahead of
   * its account-service gets a 200 with the field silently stripped, and the modal becomes
   * undismissable with nothing on screen to explain why.
   *
   * So the click always closes it, and the preference is what keeps it closed. A write that fails
   * raises the httpClient toast and the announcement returns on the next load: a second showing is a
   * far cheaper failure than a page nobody can get past.
   */
  const dismiss = () => {
    setDismissedThisSession(true);
    track(EventNames.NEW_LOOK_MODAL_DISMISSED);
    updatePreferences({ newLookModalSeen: true });
  };

  return (
    <Modal
      data-e2e="new-look-modal"
      open
      onCancel={dismiss}
      titleId="new-look.modal.title"
      subtitleId="new-look.modal.message"
      size="medium"
      centered
      destroyOnClose
      footer={
        <Button type="primary" onClick={dismiss} data-e2e="new-look-modal-dismiss">
          <FormattedMessage id="new-look.modal.cta" />
        </Button>
      }>
      <Explanation>
        <FormattedMessage id="new-look.modal.switch-back-hint" />
      </Explanation>
    </Modal>
  );
};

export default NewLookModal;
